<script setup lang="ts">
import {computed, ref, watch} from "vue";
import {healthcenterApi, laboratoryApi} from "@/container";
import {Skeleton} from "@/core/components/ui/skeleton";
import useSwr from "@/core/composables/swr/useSwr";
import ToggleSwitch from "@/program/components/support-program/SupportProgramNotificationToggle/ToggleSwitch.vue";
import {useToastStore} from "@/stores/toast.store";
import * as Sentry from "@sentry/vue";
import {cacheKey} from "@/lib/cache";

const props = defineProps<{ laboratoryId: number; healthcenterId: number }>();
const sharingLoading = ref(false);
const sharingEnabled = ref<boolean>();
const {showError, showSuccess, showInfo} = useToastStore();


const {data, isLoading} = useSwr(
    `laboratories/${props.laboratoryId}/sharing`,
    () => laboratoryApi().sharing(props.laboratoryId),
    {
        revalidateOnFocus: false
    }
);

const {data: sharing, mutate} = useSwr(
    computed(() => cacheKey(`healthcenters/${props.healthcenterId}/laboratories/${props.laboratoryId}/sharing`)),
    () => healthcenterApi().laboratorySharing(props.healthcenterId, props.laboratoryId),
    {revalidateOnFocus: false}
);

/**
 * Watch sharing data, and set sharing status only when it was not already set.
 */
watch(sharing, (value) => {
    if (value && sharingEnabled.value === undefined) {
        sharingEnabled.value = value.is_accepted;
    }
});

/**
 * Update sharing status.
 *
 * @param status
 */
const updateSharing = async (status: boolean) => {
    sharingLoading.value = true;
    try {
        await healthcenterApi().updateLaboratorySharing(props.healthcenterId, props.laboratoryId, status);
        await mutate();
        if (sharing.value && sharing.value.is_accepted) {
            showSuccess({content: 'Votre autorisation de partage de données a été validée.'});
        } else if (sharing.value && !sharing.value.is_accepted) {
            showInfo({content: 'Votre refus du partage de données a été validée'});
        }
    } catch (e) {
        Sentry.captureException(e);
        showError({content: 'Une erreur est survenue.'});
    }
    sharingLoading.value = false;
};

/**
 * Watch sharing status change, and update sharing when necessary.
 */
watch(sharingEnabled, (value) => {
    if (!sharingLoading.value && (sharing.value && value !== sharing.value.is_accepted)) {
        updateSharing(!!value);
    }
});

</script>

<template>
  <Skeleton v-if="isLoading" class="h-32 w-full" />
  <div v-else-if="data?.enabled"  class="flex items-center bg-white rounded-md shadow-md">
    <div class="flex flex-col p-4">
      <h2 class="text-black pb-2">Gestion de vos paramètres</h2>
      <div class="flex flex-1 flex-row gap-2">
        <div class="flex flex-col flex-2">
          <p class="text-sm">
            {{ data.message }}
          </p>
        </div>
        <div v-if="sharing" class="flex flex-1 justify-end gap-2 pr-4">
          <Label class="pt-[18px]">Partage {{ sharingEnabled ? "activé" : "désactivé" }}</Label>
          <ToggleSwitch
            v-model="sharingEnabled"
            :is-loading="sharingLoading"
          />
        </div>
      </div>
    </div>
  </div>
</template>
